import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../ui/input';
import { MagnifyingGlass, NavigationArrow, X } from '@phosphor-icons/react';
import { toTitleCase } from '@/utils/toTitleCase';

const buildCleanAddressLabel = (result) => {
  const addr = result.address || {};
  const street = addr.road || addr.pedestrian || addr.footway || addr.path || '';
  const houseNumber = addr.house_number || '';
  const postcode = addr.postcode || '';
  const city = addr.city || addr.town || addr.municipality || addr.village || addr.hamlet || '';
  const primary = [toTitleCase(street), houseNumber].filter(Boolean).join(' ').trim();
  const locality = [postcode, toTitleCase(city)].filter(Boolean).join(' ').trim();
  const label = [primary, locality].filter(Boolean).join(' ').trim();
  if (label) return label;
  // Fallback for sparse payloads: strip commas and compact spaces from display_name.
  return String(result.display_name || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
};

const mapNominatimResult = (result) => {
  const parts = (result.display_name || '').split(',').map((p) => p.trim()).filter(Boolean);
  const state = result.address?.state || '';
  const stateIdx = parts.findIndex((p) => p === state);
  const communeFromDisplay = stateIdx > 0 ? parts[stateIdx - 1] : '';
  const parsedLat = Number.parseFloat(result.lat);
  const parsedLon = Number.parseFloat(result.lon);
  const cleanLabel = buildCleanAddressLabel(result);

  return {
    display: cleanLabel,
    lat: Number.isFinite(parsedLat) ? parsedLat : null,
    lon: Number.isFinite(parsedLon) ? parsedLon : null,
    postcode: result.address?.postcode || '',
    city: toTitleCase(communeFromDisplay || result.address?.city || result.address?.town || result.address?.municipality || result.address?.village || ''),
  };
};

export function AddressSearch({ value, onChange, onSelect, onClear, countryCode = 'CH' }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);
  const cc = (countryCode || 'CH').toLowerCase();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const fetchSuggestions = useCallback((query) => {
    if (query.length < 3) { setSuggestions([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=${encodeURIComponent(cc)}&addressdetails=1`,
          { headers: { 'Accept-Language': 'fr' } }
        );
        const data = await res.json();
        setSuggestions(data.map(mapNominatimResult));
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    }, 300);
  }, [cc]);

  const showClear = Boolean(value && String(value).trim());

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSuggestions([]);
    setShowSuggestions(false);
    setLocationError('');
    if (onClear) onClear();
    else onChange('');
  };

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      setShowSuggestions(true);
      return;
    }

    setIsLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.latitude}&lon=${coords.longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'fr' } }
          );
          if (!res.ok) throw new Error('Reverse geocoding failed');
          const data = await res.json();
          const parsed = mapNominatimResult(data);
          if (!parsed.display) throw new Error('No address found for location');

          onSelect(parsed.display, {
            postcode: parsed.postcode,
            city: parsed.city,
            lat: parsed.lat,
            lon: parsed.lon,
            selectedAddress: parsed.display,
          });
          setSuggestions([]);
          setShowSuggestions(false);
        } catch {
          setLocationError('Could not resolve your current location to an address.');
          setShowSuggestions(true);
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) setLocationError('Location permission denied. Enable it to auto-fill your address.');
        else if (error.code === error.TIMEOUT) setLocationError('Location request timed out. Please try again.');
        else setLocationError('Unable to retrieve your location.');
        setShowSuggestions(true);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [onSelect]);

  return (
    <div ref={wrapperRef} className="relative" data-testid="address-search-wrapper">
      <div className="relative flex items-center">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-[1]" />
        <Input
          type="text" value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setLocationError('');
            fetchSuggestions(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search address or parcel..."
          aria-label="Search address or parcel"
          className={`h-11 md:h-9 pl-9 text-[13px] font-medium bg-white border-slate-300 rounded-lg w-full focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 ${showClear ? 'pr-12' : 'pr-3'}`}
          data-testid="input-address"
          autoComplete="street-address"
        />
        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:h-9 md:w-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 touch-manipulation z-[2]"
            aria-label="Clear address"
            title="Clear"
            data-testid="address-clear-btn"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        )}
      </div>
      {showSuggestions && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[180px] overflow-y-auto" data-testid="address-suggestions">
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={isLocating}
            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 border-b border-slate-100 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="address-use-location-btn"
          >
            <NavigationArrow weight="bold" className="w-3.5 h-3.5" />
            {isLocating ? 'Detecting your location...' : 'Use my current location'}
          </button>
          {locationError && (
            <div className="px-3 py-2 text-[10px] text-rose-600 border-b border-slate-100" data-testid="address-location-error">
              {locationError}
            </div>
          )}
          {suggestions.map((s, i) => (
            <button key={i}
              className="w-full text-left px-3 py-2 text-[10px] text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 border-b border-slate-50 last:border-0 leading-snug"
              onClick={() => {
                onSelect(s.display, {
                  postcode: s.postcode,
                  city: s.city,
                  lat: s.lat,
                  lon: s.lon,
                  selectedAddress: s.display,
                });
                setShowSuggestions(false);
                setSuggestions([]);
              }}
            >{s.display}</button>
          ))}
          {!isLocating && !locationError && suggestions.length === 0 && (
            <div className="px-3 py-2 text-[10px] text-slate-500">
              {value?.trim().length >= 3 ? 'No address found for this search.' : 'Type at least 3 characters or use your current location.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
